import React, { useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import omnicareLogo from '../assets/icons/logo.svg';
import '../styles/components/ResultPage.css';
import {
  CheckCircle2, ChevronDown, ChevronUp, Share2,
  ShieldCheck, BookOpen, ArrowRight, Activity,
  Heart, Download, FileText,
  Sparkles, Camera, Users, X, Twitter,
  Loader2, Image as ImageIcon
} from 'lucide-react';

// --- Utility: Dynamically load html2canvas ---
const loadHtml2Canvas = () => {
  return new Promise((resolve, reject) => {
    if (window.html2canvas) { resolve(window.html2canvas); return; }
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
    script.onload = () => resolve(window.html2canvas);
    script.onerror = () => reject(new Error('Failed to load html2canvas'));
    document.body.appendChild(script);
  });
};

// --- Utility: Dynamically load jsPDF ---
const loadJsPDF = () => {
  return new Promise((resolve, reject) => {
    if (window.jspdf) { resolve(window.jspdf.jsPDF); return; }
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
    script.onload = () => resolve(window.jspdf.jsPDF);
    script.onerror = () => reject(new Error('Failed to load jsPDF'));
    document.body.appendChild(script);
  });
};

// --- PDF Generation ---
async function generatePDF(surveyData, recommendations) {
  const jsPDF = await loadJsPDF();
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentW = pageW - margin * 2;
  let y = margin;

  const checkPage = (needed = 20) => {
    if (y + needed > pageH - 20) {
      doc.addPage();
      y = margin;
    }
  };

  const drawLine = () => {
    doc.setDrawColor(200);
    doc.line(margin, y, pageW - margin, y);
    y += 4;
  };

  // --- Header ---
  doc.setFillColor(68, 98, 74); // #44624a
  doc.rect(0, 0, pageW, 40, 'F');
  doc.setTextColor(255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('Pebble Health', margin, 18);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text('Personalized Cancer Screening Report', margin, 27);
  doc.setFontSize(9);
  doc.text(`Generated ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, margin, 34);
  y = 50;

  // --- Patient Profile ---
  doc.setTextColor(30);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Patient Profile', margin, y);
  y += 8;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const bmi = (surveyData.weightInKg && surveyData.heightInCm) 
    ? (surveyData.weightInKg / ((surveyData.heightInCm / 100) ** 2)).toFixed(1) 
    : 'N/A';
  const profileItems = [
    ['Age', surveyData.age || 'N/A'],
    ['Biological Sex', surveyData.biologicalSex || 'N/A'],
    ['Ethnicity', surveyData.ethnicity || 'N/A'],
    ['BMI', `${bmi} (${surveyData.weightInKg}kg, ${surveyData.heightInCm}cm)`],
    ['Smoking', surveyData.smokingHistory > 0 ? `${surveyData.smokingHistory} years, ${surveyData.cigarettesPerDay || 0} cig/day` : 'Non-smoker'],
    ['Alcohol', surveyData.alcoholConsumption || 'None'],
    ['Diet Concerns', surveyData.dietHabits === 'no' ? 'None' : surveyData.dietHabits || 'N/A'],
    ['Family Cancer History', Array.isArray(surveyData.familyCancerHistory) && surveyData.familyCancerHistory.filter(f => f.toLowerCase() !== 'none').length > 0 
      ? surveyData.familyCancerHistory.filter(f => f.toLowerCase() !== 'none').join(', ') 
      : 'None reported'],
    ['Chronic Conditions', Array.isArray(surveyData.chronicDiseases) && surveyData.chronicDiseases.filter(d => d.toLowerCase() !== 'none').length > 0 
      ? surveyData.chronicDiseases.filter(d => d.toLowerCase() !== 'none').join(', ') 
      : 'None reported'],
  ];

  // Two-column profile layout
  const colW = contentW / 2;
  profileItems.forEach((item, i) => {
    const col = i % 2;
    if (col === 0 && i > 0) y += 6;
    checkPage(8);
    const x = margin + col * colW;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(100);
    doc.setFontSize(8);
    doc.text(item[0].toUpperCase(), x, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(30);
    doc.setFontSize(10);
    doc.text(String(item[1]), x, y + 4);
  });
  if (profileItems.length % 2 !== 0) y += 6;
  y += 12;
  drawLine();

  // --- Disclaimer ---
  doc.setFillColor(255, 248, 230);
  checkPage(18);
  doc.roundedRect(margin, y, contentW, 14, 2, 2, 'F');
  doc.setFontSize(8);
  doc.setTextColor(120, 100, 50);
  doc.setFont('helvetica', 'italic');
  const disclaimer = 'This report is for educational purposes only. It does not constitute medical advice. Please consult your healthcare provider for personalized screening recommendations.';
  doc.text(disclaimer, margin + 4, y + 5, { maxWidth: contentW - 8 });
  y += 18;

  // --- Screening Sections ---
  const { stronglyRecommend = [], recommend = [], belowAverage = [] } = recommendations || {};

  const renderSection = (title, subtitle, items, color) => {
    if (items.length === 0) return;
    checkPage(30);
    
    // Section header
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...color);
    doc.text(title, margin, y);
    y += 5;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text(subtitle, margin, y);
    y += 8;

    items.forEach((item) => {
      checkPage(50);
      const screening = item.screening || {};
      const riskFactors = item.riskFactors || [];

      // Card background
      doc.setFillColor(250, 250, 248);
      const cardStart = y;
      // We'll draw the background after calculating height
      
      // Cancer name + risk score
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30);
      doc.text(item.cancer || 'Screening', margin + 4, y + 5);
      
      // Risk badge
      const score = (item.riskScore || 1.0).toFixed(1);
      const badgeText = `${score}x baseline`;
      doc.setFontSize(8);
      const badgeW = doc.getTextWidth(badgeText) + 6;
      const badgeX = pageW - margin - badgeW - 4;
      if (item.riskScore >= 2.5) doc.setFillColor(254, 226, 226);
      else if (item.riskScore >= 1.5) doc.setFillColor(255, 237, 213);
      else doc.setFillColor(236, 253, 245);
      doc.roundedRect(badgeX, y + 1, badgeW, 6, 1, 1, 'F');
      doc.setTextColor(80);
      doc.text(badgeText, badgeX + 3, y + 5);

      // Gov Recommended / PubMed badge
      if (item.tier === 'primary') {
        doc.setFillColor(219, 234, 254);
        doc.setTextColor(30, 64, 175);
        doc.setFontSize(7);
        const govText = 'GOV RECOMMENDED';
        const govW = doc.getTextWidth(govText) + 6;
        doc.roundedRect(margin + 4, y + 9, govW, 5, 1, 1, 'F');
        doc.text(govText, margin + 7, y + 12.5);
      }
      y += 18;

      // Description
      if (item.description) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(80);
        const descLines = doc.splitTextToSize(item.description, contentW - 8);
        checkPage(descLines.length * 4 + 4);
        doc.text(descLines, margin + 4, y);
        y += descLines.length * 4 + 2;
      }

      // Risk Factors
      if (riskFactors.length > 0) {
        checkPage(10 + riskFactors.length * 6);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(60);
        doc.text('Risk Factors:', margin + 4, y);
        y += 5;
        doc.setFont('helvetica', 'normal');
        riskFactors.forEach(rf => {
          checkPage(8);
          const factor = rf.factor || rf;
          const detail = rf.detail ? ` — ${rf.detail}` : '';
          const mult = rf.multiplier ? ` (${rf.multiplier.toFixed(1)}x)` : '';
          doc.setFontSize(8);
          doc.setTextColor(80);
          const rfText = `• ${factor}${mult}${detail}`;
          const rfLines = doc.splitTextToSize(rfText, contentW - 16);
          doc.text(rfLines, margin + 8, y);
          y += rfLines.length * 3.5 + 1;
        });
        y += 2;
      }

      // Screening Guidelines
      if (screening.method) {
        checkPage(30);
        doc.setFillColor(245, 247, 250);
        doc.roundedRect(margin + 4, y, contentW - 8, 4, 1, 1, 'F');
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(60);
        doc.text('Screening Guidelines', margin + 8, y + 3);
        y += 7;
        
        const screeningDetails = [
          ['Method', screening.method],
          ['Who Should Screen', screening.who],
          ['Interval', screening.interval],
          ['Guideline', screening.guideline],
          ['Source', screening.source],
        ].filter(([, v]) => v);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        screeningDetails.forEach(([label, value]) => {
          checkPage(8);
          doc.setTextColor(100);
          doc.setFont('helvetica', 'bold');
          doc.text(`${label}:`, margin + 8, y);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(60);
          const valLines = doc.splitTextToSize(String(value), contentW - 40);
          doc.text(valLines, margin + 35, y);
          y += valLines.length * 3.5 + 1.5;
        });

        if (screening.highRisk) {
          checkPage(10);
          doc.setFillColor(255, 248, 230);
          doc.setFontSize(8);
          doc.setFont('helvetica', 'italic');
          doc.setTextColor(120, 100, 50);
          const hrLines = doc.splitTextToSize(`High-risk note: ${screening.highRisk}`, contentW - 16);
          doc.roundedRect(margin + 8, y - 1, contentW - 16, hrLines.length * 3.5 + 4, 1, 1, 'F');
          doc.text(hrLines, margin + 10, y + 2);
          y += hrLines.length * 3.5 + 5;
        }
      }

      // Statistics
      if (item.statistics && item.statistics.length > 0) {
        checkPage(8 + item.statistics.length * 5);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(60);
        doc.text('Key Statistics:', margin + 4, y);
        y += 5;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        item.statistics.forEach(s => {
          checkPage(5);
          doc.setTextColor(80);
          doc.text(`• ${s.label}: ${s.value}`, margin + 8, y);
          y += 4;
        });
        y += 2;
      }

      // Guideline source
      if (item.guidelineSource && item.guidelineSource.org) {
        checkPage(6);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(140);
        doc.text(`Source: ${item.guidelineSource.org}${item.guidelineSource.note ? ' — ' + item.guidelineSource.note : ''}`, margin + 4, y);
        y += 5;
      }

      // Card bottom line
      y += 4;
      drawLine();
      y += 2;
    });
  };

  renderSection(
    'Strongly Recommended Screenings',
    'Elevated risk detected — take action soon',
    stronglyRecommend,
    [185, 28, 28] // red
  );

  renderSection(
    'Recommended Screenings', 
    'Worth discussing with your healthcare provider',
    recommend,
    [180, 120, 20] // amber
  );

  renderSection(
    'Routine Monitoring',
    'At or below average risk — stay on schedule',
    belowAverage,
    [80, 120, 80] // muted green
  );

  // --- Footer on every page ---
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(160);
    doc.setFont('helvetica', 'normal');
    doc.text('Pebble Health — pebblehealth.ai', margin, pageH - 10);
    doc.text(`Page ${i} of ${totalPages}`, pageW - margin - 20, pageH - 10);
    doc.text('This report is not a substitute for professional medical advice.', margin, pageH - 6);
  }

  doc.save('pebble-health-screening-report.pdf');
}

// --- Reusable Components ---
const Badge = ({ children, color = "green", className = "" }) => {
  const colors = {
    green: "bg-[#f0f4f1] text-[#3a5440] border-[#c8e6c9]",
    orange: "bg-orange-100 text-orange-800 border-orange-200",
    stone: "bg-stone-200 text-stone-800 border-stone-300",
    purple: "bg-purple-100 text-purple-800 border-purple-200",
    red: "bg-red-100 text-red-800 border-red-200",
  };
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-bold tracking-wider uppercase border ${colors[color] || colors.green} ${className}`}>
      {children}
    </span>
  );
};

const ExpandableScienceCard = ({ data }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const Icon = data.icon;
  return (
    <div className="bg-white border-2 border-stone-200 rounded-3xl p-6 sm:p-8 transition-all hover:border-stone-300 hover:shadow-sm">
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-4">
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-2xl ${data.statusColor === 'orange' ? 'bg-orange-100 text-orange-600' : data.statusColor === 'red' ? 'bg-red-100 text-red-600' : 'bg-[#f0f4f1] text-[#3a5440]'}`}>
            <Icon size={28} strokeWidth={2.5} />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge color={data.statusColor}>{data.priority}</Badge>
              {data.multiplier && (
                <span className="text-xs font-bold tracking-wider uppercase text-stone-500 bg-stone-100 px-3 py-1 rounded-full border border-stone-200">
                  {data.multiplier}x Baseline
                </span>
              )}
              {data.isGovRecommended ? (
                <span className="text-xs font-bold tracking-wider uppercase text-blue-700 bg-blue-50 px-3 py-1 rounded-full border border-blue-200 flex items-center gap-1">
                  <ShieldCheck size={12} /> Gov Recommended
                </span>
              ) : (
                <span className="text-xs font-bold tracking-wider uppercase text-violet-600 bg-violet-50 px-3 py-1 rounded-full border border-violet-200 flex items-center gap-1">
                  <BookOpen size={12} /> PubMed Cited
                </span>
              )}
            </div>
            <h3 className="font-serif text-2xl font-bold text-stone-900 mt-2">{data.title}</h3>
          </div>
        </div>
      </div>
      <p className="text-stone-600 text-lg leading-relaxed">{data.summary}</p>
      {data.clinicalText && (
        <div className="mt-6 pt-6 border-t border-stone-100">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-2 text-stone-500 hover:text-stone-800 font-semibold transition-colors"
          >
            <BookOpen size={18} />
            {isExpanded ? "Hide Details" : "Why this matters — the science"}
            {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          {isExpanded && (
            <div className="mt-4 p-5 bg-stone-50 rounded-2xl border border-stone-200 text-sm">
              <h4 className="font-bold text-stone-900 mb-2">What This Means for You:</h4>
              <div className="text-stone-700 mb-4 whitespace-pre-line">{data.clinicalText}</div>
              {data.sources && data.sources.filter(Boolean).length > 0 && (
                <>
                  <h4 className="font-bold text-stone-900 mb-2">Where This Comes From:</h4>
                  <ul className="space-y-2">
                    {data.sources.filter(Boolean).map((source, i) => (
                      <li key={i} className="flex gap-2 text-stone-600">
                        <span className="text-stone-400 font-serif">[{i + 1}]</span>
                        <span>{source}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// --- Share Modal ---
const ShareModal = ({ isOpen, onClose, imageUrl }) => {
  if (!isOpen) return null;
  const handleDownload = () => {
    const link = document.createElement('a');
    link.download = 'pebblehealth-archetype.png';
    link.href = imageUrl;
    link.click();
  };
  const handleTwitterShare = () => {
    const text = encodeURIComponent("Just got my proactive health blueprint from Pebble Health ✨ Discover your archetype:");
    const url = encodeURIComponent("https://pebblehealth.ai");
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
  };
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-stone-900/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl w-full max-w-lg shadow-2xl flex flex-col overflow-hidden">
        <div className="p-4 border-b border-stone-100 flex justify-between items-center bg-stone-50">
          <h3 className="font-bold text-stone-900 flex items-center gap-2">
            <Sparkles size={18} className="text-[#3a5440]" /> Share Your Vibe
          </h3>
          <button onClick={onClose} className="p-2 text-stone-400 hover:text-stone-700 hover:bg-stone-200 rounded-full transition-colors">
            <X size={20} />
          </button>
        </div>
        <div className="p-6 bg-[#F9F8F6] flex flex-col items-center">
          <p className="text-sm text-stone-500 mb-4 text-center">Save this image to share on your Instagram or TikTok story!</p>
          <div className="w-full max-w-[280px] rounded-2xl overflow-hidden shadow-xl border-4 border-white mb-8 transform transition-transform hover:scale-105">
            {imageUrl ? (
              <img src={imageUrl} alt="Your Pebble Health Archetype" className="w-full h-auto object-cover" />
            ) : (
              <div className="w-full aspect-[4/5] bg-stone-200 animate-pulse flex items-center justify-center">
                <Loader2 className="animate-spin text-stone-400" size={32} />
              </div>
            )}
          </div>
          <div className="w-full space-y-3">
            <button onClick={handleDownload} className="w-full py-4 px-6 bg-[#44624a] hover:bg-[#44624a] text-white rounded-xl font-bold flex items-center justify-center gap-3 transition-colors active:scale-[0.98]">
              <ImageIcon size={20} /> Save Image for Stories
            </button>
            <button onClick={handleTwitterShare} className="w-full py-4 px-6 bg-stone-900 hover:bg-black text-white rounded-xl font-bold flex items-center justify-center gap-3 transition-colors active:scale-[0.98]">
              <Twitter size={20} /> Share to X
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Cancer-specific warm copy & CTAs ---
// Cancer-specific copy — short and actionable. The badges + colors already convey severity.
const CANCER_COPY = {
  lung: {
    elevated: "A low-dose CT scan is quick, painless, and catches issues when they're most treatable.",
    routine: "No immediate screening needed. If you ever become a long-term smoker, ask about low-dose CT.",
    action: { elevated: "Ask about a low-dose CT scan", routine: "Learn about lung screening" },
  },
  breast: {
    elevated: "Mammograms are one of the most effective screening tools — and they only take about 20 minutes.",
    routine: "Regular mammograms at the recommended age are simple, fast, and genuinely lifesaving.",
    action: { elevated: "Schedule a mammogram", routine: "Check your mammogram timeline" },
  },
  cervical: {
    elevated: "A Pap test with HPV co-testing is the gold standard — one of the most preventable cancers when caught early.",
    routine: "Great news: cervical cancer is one of the most preventable cancers. Stay on top of your routine Pap + HPV tests.",
    action: { elevated: "Book your Pap + HPV test", routine: "Confirm you're up to date" },
  },
  colorectal: {
    elevated: "Multiple options available — from simple at-home stool tests to colonoscopy. Talk to your doctor about what fits you.",
    routine: "Screening starts at age 45 for most people. Several options from at-home kits to colonoscopy.",
    action: { elevated: "Discuss early screening options", routine: "Plan your first screening" },
  },
  gastric: {
    elevated: "Proactive screening could make a real difference. An upper endoscopy is the most common first step.",
    routine: "If you develop persistent digestive symptoms or a family history surfaces, bring it up with your doctor.",
    action: { elevated: "Ask about an endoscopy", routine: "Know the warning signs" },
  },
  liver: {
    elevated: "Regular monitoring with ultrasound and blood tests (AFP) can catch changes early. Especially important with hepatitis B/C.",
    routine: "Healthy weight, limited alcohol, and up-to-date hepatitis vaccines are your best preventive steps.",
    action: { elevated: "Ask about liver monitoring", routine: "Review your hepatitis status" },
  },
  thyroid: {
    elevated: "Highly treatable — a simple neck exam and ultrasound can catch most issues early.",
    routine: "Routine physical exams with a neck check are usually sufficient. No special screening needed.",
    action: { elevated: "Request a thyroid check", routine: "Stay aware during checkups" },
  },
  skin: {
    elevated: "Annual full-body skin exams plus regular self-checks are your best defense. Most skin cancers are completely curable when caught early.",
    routine: "Wear sunscreen, do monthly self-checks for new or changing moles, and see a dermatologist if anything looks off.",
    action: { elevated: "Book a skin exam", routine: "Start monthly self-checks" },
  },
  prostate: {
    elevated: "A simple PSA blood test is the first step — quick and straightforward.",
    routine: "The PSA test is standard; talk to your doctor about when to start based on your age.",
    action: { elevated: "Ask about a PSA test", routine: "Plan your PSA timeline" },
  },
  ovarian: {
    elevated: "No perfect screening test yet, but CA-125 blood tests and transvaginal ultrasound can help — especially with family history.",
    routine: "No routine screening for average risk. Report any persistent bloating, pelvic pain, or changes to your doctor.",
    action: { elevated: "Discuss monitoring options", routine: "Know the symptoms" },
  },
  uterine: {
    elevated: "Report any unexpected vaginal bleeding promptly — early detection makes a huge difference.",
    routine: "Stay attentive to any unusual bleeding and keep up with regular gynecological exams.",
    action: { elevated: "Talk to your gynecologist", routine: "Stay up to date on exams" },
  },
  testicular: {
    elevated: "One of the most treatable cancers when caught early. Monthly self-exams take less than a minute.",
    routine: "Self-exams are simple and effective — highest cure rate when detected early.",
    action: { elevated: "Learn the self-exam technique", routine: "Start monthly self-exams" },
  },
};

const DEFAULT_COPY = {
  elevated: "Early detection is one of the most powerful tools in cancer prevention. Discuss screening with your doctor.",
  routine: "Staying on top of routine screenings is the best way to keep things on track.",
  action: { elevated: "Talk to your doctor", routine: "Plan your screening" },
};

// --- Helper: Map recommendation data to v2 card format ---
function mapRecommendationsToCards(recommendations, surveyData) {
  const { stronglyRecommend = [], recommend = [], belowAverage = [] } = recommendations || {};
  const allItems = [...stronglyRecommend, ...recommend];
  
  const iconMap = {
    gastric: Activity,
    liver: Activity,
    lung: ShieldCheck,
    breast: Heart,
    cervical: ShieldCheck,
    colorectal: Activity,
    thyroid: ShieldCheck,
    skin: ShieldCheck,
    prostate: ShieldCheck,
    ovarian: Heart,
    uterine: Heart,
    testicular: ShieldCheck,
  };

  return allItems.map((item) => {
    const score = item.riskScore || 1.0;
    const isElevated = score > 1.2;
    let statusColor = 'green';
    let priority = 'On Your Radar';
    if (score >= 2.5) { statusColor = 'red'; priority = 'Act Now'; }
    else if (score >= 1.5) { statusColor = 'orange'; priority = 'Worth a Conversation'; }
    else if (score > 1.2) { statusColor = 'stone'; priority = 'Good to Know'; }

    const riskFactors = item.riskFactors || [];
    const screening = item.screening || {};
    const copy = CANCER_COPY[item.cancerKey] || DEFAULT_COPY;

    // Sources — filter out any undefined/empty values
    const sources = [];
    if (item.guidelineSource && item.guidelineSource.org) {
      sources.push(`${item.guidelineSource.org}${item.guidelineSource.note ? ': ' + item.guidelineSource.note : ''}`);
    }
    if (screening.source) sources.push(screening.source);
    if (screening.guideline && !sources.length) sources.push(screening.guideline);

    // Clinical detail text (for the expandable section)
    const clinicalParts = [];
    if (riskFactors.length > 0) {
      clinicalParts.push("What we found in your profile:");
      riskFactors.forEach(r => {
        const factor = r.factor || r;
        const detail = r.detail || '';
        clinicalParts.push(`• ${factor}${detail ? ' — ' + detail : ''}`);
      });
    }
    if (screening.method) {
      clinicalParts.push('');
      clinicalParts.push(`Recommended screening: ${screening.method}`);
      if (screening.interval) clinicalParts.push(`How often: ${screening.interval}`);
      if (screening.who) clinicalParts.push(`Who should get it: ${screening.who}`);
      if (screening.guideline) clinicalParts.push(`Guideline: ${screening.guideline}`);
    }
    if (screening.highRisk) {
      clinicalParts.push('');
      clinicalParts.push(`For higher-risk individuals: ${screening.highRisk}`);
    }
    // Statistics
    if (item.statistics && item.statistics.length > 0) {
      clinicalParts.push('');
      clinicalParts.push('By the numbers:');
      item.statistics.forEach(s => clinicalParts.push(`• ${s.label}: ${s.value}`));
    }

    return {
      title: item.cancer || 'Screening',
      priority,
      multiplier: score.toFixed(1),
      statusColor,
      icon: iconMap[item.cancerKey] || ShieldCheck,
      summary: isElevated ? copy.elevated : copy.routine,
      action: isElevated ? copy.action.elevated : copy.action.routine,
      clinicalText: clinicalParts.join('\n'),
      sources,
      // Government guideline flags
      isGovRecommended: item.tier === 'primary',
      guidelineSource: item.guidelineSource || null,
    };
  });
}

// --- Helper: Determine archetype from survey data ---
// 7 archetypes based on lifestyle score + bonus flags (former smoker, family history)
// Only references data we actually collect. No guessing.
//
// Decision tree:
//   Former smoker + score ≥ 3  → Phoenix 🔥
//   Score 5 + no family history → Zen Master 🧘
//   Score 5 + family history    → Sentinel 🛡️
//   Score 4                     → Adventurer 🧭
//   Score 2-3 + alcohol/diet    → Rebel 🎸
//   Score 2-3 + other factors   → Night Owl 🦉
//   Score 0-1                   → Seedling 🌱

function getArchetype(surveyData) {
  if (!surveyData) return { name: "The Seedling", emoji: "🌱", superpower: "Taking the first step", watchOut: "The unknown", percentile: "Top 60%" };
  
  const { weightInKg, heightInCm, smokingHistory, cigarettesPerDay, alcoholConsumption, dietHabits, familyCancerHistory = [], livedWithSmoker } = surveyData;
  
  // --- Calculate the 5 lifestyle factors ---
  const bmi = (weightInKg && heightInCm) ? weightInKg / ((heightInCm / 100) ** 2) : null;
  const isHealthyBmi = bmi && bmi >= 18.5 && bmi < 25;
  const isNonSmoker = (!smokingHistory || smokingHistory === 0) && (!cigarettesPerDay || cigarettesPerDay === 0);
  const isLowAlcohol = !alcoholConsumption || alcoholConsumption === "none" || alcoholConsumption === "under_7";
  const hasGoodDiet = !dietHabits || dietHabits === 'no' || dietHabits === 'unsure';
  const noSecondhandSmoke = livedWithSmoker !== 'yes';
  
  const healthScore = [isHealthyBmi, isNonSmoker, isLowAlcohol, hasGoodDiet, noSecondhandSmoke].filter(Boolean).length;
  
  // --- Bonus flags ---
  const isFormerSmoker = (smokingHistory > 0) && (!cigarettesPerDay || cigarettesPerDay === 0);
  const filteredFamily = (Array.isArray(familyCancerHistory) ? familyCancerHistory : [])
    .filter(fh => fh.toLowerCase() !== 'none');
  const hasFamilyHistory = filteredFamily.length > 0;
  const alcoholIsWeakSpot = !isLowAlcohol; // 7_to_14 or higher
  const dietIsWeakSpot = !hasGoodDiet; // any yes_* or multiple
  
  // --- Determine the biggest weak spot from actual data ---
  const weakSpot = !isNonSmoker ? "Smoking exposure" 
    : !isLowAlcohol ? "Alcohol intake" 
    : !hasGoodDiet ? "Processed food habits"
    : !isHealthyBmi ? "Weight management"
    : !noSecondhandSmoke ? "Secondhand smoke exposure"
    : hasFamilyHistory ? "Family history — not your fault, but worth screening for"
    : "Complacency — don't skip your checkups!";

  // --- Decision tree ---
  
  // 🔥 Phoenix: Former smoker who turned their life around (score ≥ 3)
  if (isFormerSmoker && healthScore >= 3) {
    return {
      name: "The Phoenix",
      emoji: "🔥",
      superpower: "You made the hardest change already",
      watchOut: weakSpot,
      percentile: "Top 15%",
      tagline: "Rose from the ashes",
    };
  }
  
  // 🧘 Zen Master: Perfect 5/5, no family history baggage
  if (healthScore === 5 && !hasFamilyHistory) {
    return {
      name: "The Zen Master",
      emoji: "🧘",
      superpower: "Balance in all things",
      watchOut: weakSpot,
      percentile: "Top 8%",
      tagline: "Calm, in control",
    };
  }
  
  // 🛡️ Sentinel: Perfect 5/5, but fighting genetics
  if (healthScore === 5 && hasFamilyHistory) {
    return {
      name: "The Sentinel",
      emoji: "🛡️",
      superpower: "Proactive despite the odds",
      watchOut: weakSpot,
      percentile: "Top 10%",
      tagline: "Guarding against what you can't control",
    };
  }
  
  // 🧭 Adventurer: 4/5, one indulgence
  if (healthScore === 4) {
    return {
      name: "The Adventurer",
      emoji: "🧭",
      superpower: "Life's for living — and you're mostly nailing it",
      watchOut: weakSpot,
      percentile: "Top 20%",
      tagline: "Mostly solid, one indulgence",
    };
  }
  
  // Score 2-3: Split between Rebel and Night Owl
  if (healthScore >= 2) {
    // 🎸 Rebel: Weak spots are lifestyle choices (alcohol or diet)
    if (alcoholIsWeakSpot || dietIsWeakSpot) {
      return {
        name: "The Rebel",
        emoji: "🎸",
        superpower: "You know the risks and you own them",
        watchOut: weakSpot,
        percentile: "Top 35%",
        tagline: "Lives a little, knows the score",
      };
    }
    // 🦉 Night Owl: Weak spots are more circumstantial
    return {
      name: "The Night Owl",
      emoji: "🦉",
      superpower: "Self-aware and honest about where you stand",
      watchOut: weakSpot,
      percentile: "Top 40%",
      tagline: "Eyes wide open",
    };
  }
  
  // 🌱 Seedling: 0-1 score
  return {
    name: "The Seedling",
    emoji: "🌱",
    superpower: "The hardest step is the first one — you just took it",
    watchOut: weakSpot,
    percentile: "Top 60%",
    tagline: "Everything ahead of you",
  };
}

// --- Helper: Get "wins" from survey data ---
// Only show wins that are genuinely meaningful. Max 3 to avoid dilution.
function getWins(surveyData) {
  if (!surveyData) return [];
  const wins = [];
  const { weightInKg, heightInCm, smokingHistory, cigarettesPerDay, alcoholConsumption, dietHabits, livedWithSmoker } = surveyData;
  
  const bmi = (weightInKg && heightInCm) ? weightInKg / ((heightInCm / 100) ** 2) : null;
  
  // BMI — only show if genuinely in healthy range (not borderline)
  if (bmi && bmi >= 18.5 && bmi < 25) {
    wins.push({ 
      title: `Healthy BMI (${bmi.toFixed(1)})`, 
      desc: "A healthy weight lowers your risk for at least 13 types of cancer. You're in a great spot." 
    });
  }
  
  // Non-smoker — this is a big deal, worth celebrating
  if ((!smokingHistory || smokingHistory === 0) && (!cigarettesPerDay || cigarettesPerDay === 0)) {
    wins.push({ 
      title: "Smoke-Free", 
      desc: "Smoking is the #1 preventable cause of cancer. Being smoke-free is the single most protective thing you can do." 
    });
  }
  
  // Good diet — only if they explicitly said no to processed foods
  if (dietHabits === 'no') {
    wins.push({ 
      title: "Whole-Food Diet", 
      desc: "Avoiding processed and ultra-processed foods is linked to lower risk of colorectal, gastric, and liver cancers." 
    });
  }
  
  // Low alcohol — only if truly minimal
  if (!alcoholConsumption || alcoholConsumption === "none") {
    wins.push({ 
      title: "Low Alcohol Intake", 
      desc: "Even moderate drinking increases cancer risk. Keeping it minimal puts you ahead of the curve." 
    });
  }
  
  // If no meaningful wins, acknowledge showing up honestly
  if (wins.length === 0) {
    wins.push({ 
      title: "You Showed Up", 
      desc: "Taking this quiz means you're thinking about prevention. That's the most important first step." 
    });
  }
  
  return wins.slice(0, 3); // Max 3 — keep it impactful
}

// --- Main Component ---
export default function ResultPageV2() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const cardRef = useRef(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [showMorePriorities, setShowMorePriorities] = useState(false);

  // Read data from localStorage (same source as v1)
  const analysisResults = useMemo(() => {
    try {
      const stored = localStorage.getItem("omnicare_analysis_results");
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  }, []);

  const { surveyData, recommendations } = analysisResults || {};
  const prioritiesData = useMemo(() => {
    const cards = mapRecommendationsToCards(recommendations, surveyData);
    // Sort: gov-recommended items first, then by risk score descending
    // This ensures gov items are always in the visible top 3
    return cards.sort((a, b) => {
      if (a.isGovRecommended && !b.isGovRecommended) return -1;
      if (b.isGovRecommended && !a.isGovRecommended) return 1;
      return parseFloat(b.multiplier) - parseFloat(a.multiplier);
    });
  }, [recommendations, surveyData]);
  const archetype = useMemo(() => getArchetype(surveyData), [surveyData]);
  const wins = useMemo(() => getWins(surveyData), [surveyData]);

  const handleCaptureCard = async () => {
    if (!cardRef.current) return;
    setIsCapturing(true);
    try {
      const html2canvas = await loadHtml2Canvas();
      const element = cardRef.current;
      const canvas = await html2canvas(element, {
        scale: 3,
        useCORS: true,
        backgroundColor: '#064e3b',
        logging: false,
        width: element.offsetWidth,
        height: element.offsetHeight,
      });
      const imgDataUrl = canvas.toDataURL('image/png');
      setCapturedImage(imgDataUrl);
      setShareModalOpen(true);
    } catch (error) {
      console.error("Error capturing card:", error);
    } finally {
      setIsCapturing(false);
    }
  };

  // If no data, redirect
  if (!analysisResults) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
        <div className="text-center space-y-4">
          <h2 className="font-serif text-2xl font-bold text-stone-900">No results found</h2>
          <p className="text-stone-600">Complete the health screening quiz first.</p>
          <button onClick={() => navigate('/survey/1')} className="px-6 py-3 bg-[#44624a] text-white rounded-xl font-bold">
            Take the Quiz
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6] text-stone-900 font-sans selection:bg-[#c2d5c4]">
      {/* Header — matches other pages */}
      <header className="result-page-header" style={{ background: 'var(--lp-surface, #fff)', borderBottom: '1px solid var(--lp-border, #e5e5e5)' }}>
        <div className="logo-section">
          <div className="logo" onClick={() => navigate('/landing')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <img src={omnicareLogo} alt="Pebble Health Logo" style={{ width: 28, height: 28, opacity: 0.9 }} />
            <span style={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em', color: 'var(--lp-text, #1a1a1a)' }}>Pebble Health</span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={() => navigate('/results')}
            className="hidden sm:flex"
            style={{ alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, background: '#f5f5f5', padding: '6px 14px', borderRadius: 999, border: 'none', cursor: 'pointer', color: '#666' }}
          >
            Classic View
          </button>
          <button
            onClick={handleCaptureCard}
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, background: '#f0f4f1', padding: '6px 14px', borderRadius: 999, border: 'none', cursor: 'pointer', color: '#44624a' }}
          >
            <Share2 size={14} /> Share
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        {/* 
          Mobile order: Priorities → Wins → Hero+Card → PDF
          Desktop order: Hero+Card → Wins → Priorities → PDF
          Using Tailwind order classes to reorder without duplicating JSX
        */}
        <div className="flex flex-col gap-16">

        {/* HERO SECTION — mobile: order-2, desktop: order-1 */}
        <section className="flex flex-col md:flex-row gap-12 items-center justify-between order-2 md:order-1">
          <div className="md:w-3/5 space-y-6">
            <Badge color="purple" className="animate-pulse">Insights Generated ✨</Badge>
            <h1 className="font-serif text-5xl sm:text-6xl font-extrabold leading-tight text-stone-900">
              Your proactive <br />
              <span className="text-[#2f4536] bg-[#f0f4f1]/50 px-2 rounded-lg">health blueprint.</span>
            </h1>
            <p className="text-xl text-stone-600 leading-relaxed max-w-xl">
              {prioritiesData.some(p => p.statusColor === 'red')
                ? "We found a few things worth acting on soon. Nothing to panic about — but having a conversation with your doctor this year could make a real difference."
                : prioritiesData.some(p => p.statusColor === 'orange')
                ? "Your lifestyle looks solid overall. We've flagged a couple of areas where early screening could give you extra peace of mind."
                : "Your profile looks great. We've mapped out a simple screening timeline to help you stay ahead — no surprises, just a plan."
              }
            </p>
          </div>

          {/* Viral Archetype Card */}
          <div className="w-full md:w-1/3 perspective-1000 relative group">
            <div
              ref={cardRef}
              className="bg-gradient-to-br from-[#44624a] via-[#3a5440] to-[#2f4536] rounded-3xl p-8 text-white shadow-2xl transform transition-transform duration-500 group-hover:-rotate-1 border border-[#6b8f71]/30 aspect-[4/5] flex flex-col justify-between"
            >
              <div className="flex justify-between items-start">
                <Badge color="green" className="bg-white/15 text-white/90 border-white/20 backdrop-blur-md">
                  Your Health Profile
                </Badge>
                <span className="text-3xl">{archetype.emoji}</span>
              </div>
              <div className="space-y-2 mt-8">
                <h2 className="font-serif text-5xl font-bold leading-none tracking-tight">
                  {archetype.name.split(' ').slice(0, 1)}<br />{archetype.name.split(' ').slice(1).join(' ')}
                </h2>
                {archetype.tagline && (
                  <p className="text-white/40 text-sm italic mt-1">{archetype.tagline}</p>
                )}
                <p className="text-white/70 text-sm font-medium pt-2 flex items-center gap-2">
                  <Users size={14} /> {archetype.percentile} of Pebble Health users
                </p>
              </div>
              <div className="space-y-3 mt-8">
                <div className="bg-white/10 rounded-xl p-4 backdrop-blur-md border border-white/10">
                  <p className="text-xs text-[#a3c9a8] uppercase tracking-wider font-bold mb-1">Your Strength</p>
                  <p className="font-semibold text-white">{archetype.superpower}</p>
                </div>
                <div className="bg-white/10 rounded-xl p-4 backdrop-blur-md border border-white/10">
                  <p className="text-xs text-orange-300 uppercase tracking-wider font-bold mb-1">Watch Out For</p>
                  <p className="font-semibold text-white">{archetype.watchOut}</p>
                </div>
              </div>
              <div className="mt-8 pt-6 border-t border-white/10 flex justify-between items-end">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-white rounded flex items-center justify-center">
                    <Activity className="text-[#2f4536]" size={14} />
                  </div>
                  <span className="font-serif font-bold text-sm tracking-tight text-white/80">Pebble Health</span>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-4 right-4 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10">
              <button
                onClick={handleCaptureCard}
                disabled={isCapturing}
                className="bg-white text-[#2f4536] px-5 py-3 rounded-full text-sm font-bold flex items-center gap-2 shadow-xl hover:bg-stone-50 transition-transform hover:scale-105 active:scale-95 disabled:opacity-80"
              >
                {isCapturing ? <Loader2 size={18} className="animate-spin" /> : <Camera size={18} />}
                {isCapturing ? "Generating..." : "Add to Story"}
              </button>
            </div>
          </div>
        </section>

        {/* WINS SECTION — mobile: order-3, desktop: order-2 */}
        {wins.length > 0 && (
          <section className="order-3 md:order-2">
            <div className="flex items-center gap-3 mb-6">
              <h2 className="font-serif text-2xl font-bold">What's working in your favor</h2>
              <div className="h-px bg-stone-200 flex-1"></div>
            </div>
            <div className={`grid grid-cols-1 ${wins.length >= 3 ? 'md:grid-cols-3' : wins.length === 2 ? 'md:grid-cols-2' : ''} gap-4`}>
              {wins.map((win, i) => (
                <div key={i} className="bg-[#f2f5f3] border border-[#d5e0d7] rounded-2xl p-5 flex items-start gap-4">
                  <CheckCircle2 className="text-[#3a5440] shrink-0" size={24} />
                  <div>
                    <h4 className="font-bold text-stone-900">{win.title}</h4>
                    <p className="text-sm text-stone-600 mt-1">{win.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* PRIORITIES / ACTION ZONE — mobile: order-1 (FIRST!), desktop: order-3 */}
        <section className="space-y-6 order-1 md:order-3">
          <div className="flex items-center gap-3 mb-8">
            <h2 className="font-serif text-3xl font-bold">Your Priorities</h2>
            <div className="h-px bg-stone-200 flex-1"></div>
          </div>
          <div className="grid gap-6">
            {prioritiesData.length > 0 ? (
              <>
                {prioritiesData.slice(0, showMorePriorities ? 5 : 3).map((data, index) => (
                  <ExpandableScienceCard key={index} data={data} />
                ))}
                {prioritiesData.length > 3 && !showMorePriorities && (
                  <button
                    onClick={() => setShowMorePriorities(true)}
                    className="w-full py-4 px-6 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 transition-colors border border-stone-200"
                  >
                    <ChevronDown size={18} />
                    Show {Math.min(prioritiesData.length - 3, 2)} more screening{prioritiesData.length - 3 > 1 ? 's' : ''}
                  </button>
                )}
                {showMorePriorities && prioritiesData.length > 5 && (
                  <p className="text-center text-sm text-stone-400">
                    {prioritiesData.length - 5} more screening{prioritiesData.length - 5 > 1 ? 's' : ''} available in your full PDF report
                  </p>
                )}
              </>
            ) : (
              <div className="bg-white border-2 border-stone-200 rounded-3xl p-8 text-center">
                <ShieldCheck className="text-[#3a5440] mx-auto mb-4" size={48} />
                <h3 className="font-serif text-2xl font-bold text-stone-900 mb-2">All Clear!</h3>
                <p className="text-stone-600 text-lg">No elevated risk factors detected. Keep up the great work!</p>
              </div>
            )}
          </div>
        </section>

        {/* DOWNLOAD FULL REPORT — mobile: order-4, desktop: order-4 */}
        <section className="pt-12 pb-8 order-4">
          <div className="bg-[#3a5440] rounded-3xl p-8 sm:p-12 text-center text-white shadow-xl relative overflow-hidden">
            <div className="absolute top-0 right-0 opacity-10 transform translate-x-1/4 -translate-y-1/4 pointer-events-none">
              <FileText size={250} />
            </div>
            <div className="relative z-10 max-w-2xl mx-auto space-y-6">
              <div className="w-16 h-16 bg-[#44624a] rounded-2xl flex items-center justify-center mx-auto mb-2 shadow-inner border border-[#6b8f71]/30">
                <FileText size={32} className="text-[#a3c9a8]" />
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold">Want the full clinical breakdown?</h2>
              <p className="text-[#edf2ee] text-lg leading-relaxed">
                Download a professional PDF of your screening recommendations to bring to your next doctor's appointment.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-6">
                <button 
                  onClick={() => generatePDF(surveyData, recommendations)}
                  className="w-full sm:w-auto px-8 py-4 bg-white text-[#2f4536] rounded-xl font-bold flex items-center justify-center gap-2 transition-transform hover:-translate-y-1 hover:shadow-lg active:translate-y-0"
                >
                  <Download size={20} /> Download PDF Report
                </button>
              </div>
            </div>
          </div>
        </section>

        </div>{/* end flex container */}

        {/* Switch to V1 on mobile */}
        <div className="sm:hidden text-center pb-8">
          <button
            onClick={() => navigate('/results')}
            className="text-stone-500 underline text-sm"
          >
            View Classic Results Page →
          </button>
        </div>
      </main>

      <ShareModal isOpen={shareModalOpen} onClose={() => setShareModalOpen(false)} imageUrl={capturedImage} />
    </div>
  );
}
